import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import { useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import axios from "axios";
import {
  ClipboardCheck, Plus, Trash2, Edit, Send, HelpCircle, BarChart3,
  FileText, CheckCircle, XCircle, Clock, AlertTriangle, Eye, Save,
  ChevronLeft, Users, Award, Loader2, X, Download, UserPlus, ChevronDown, ChevronRight, Tag
} from "lucide-react";
import { Button } from "../components/ui/button";
import VisibilitySelector from "../components/VisibilitySelector";
import { toast } from "sonner";

const TABS = { EXAMENES: "examenes", BANCO: "banco", CATEGORIAS: "categorias", RESULTADOS: "resultados" };

export default function ExamenesPage() {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === "admin" || user?.role === "Administrador";
  const isGH = (user?.department || "").toLowerCase().includes("gesti") || (user?.department || "").toLowerCase().includes("rrhh");
  const canManage = isAdmin || isGH;

  const getInitialTab = () => {
    if (location.pathname.includes("banco-preguntas")) return TABS.BANCO;
    if (location.pathname.includes("resultados")) return TABS.RESULTADOS;
    return TABS.EXAMENES;
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  const tabs = [
    { id: TABS.EXAMENES, label: "Exámenes", icon: FileText },
    ...(canManage ? [{ id: TABS.BANCO, label: "Banco de Preguntas", icon: HelpCircle }] : []),
    ...(canManage ? [{ id: TABS.CATEGORIAS, label: "Categorías", icon: Tag }] : []),
    { id: TABS.RESULTADOS, label: "Resultados", icon: BarChart3 }
  ];

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-14 md:pt-6 overflow-auto" data-testid="examenes-page">
        <div className="flex items-center gap-3 mb-6">
          <ClipboardCheck className="w-7 h-7 text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Exámenes y Pruebas</h1>
        </div>
        <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto mb-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"
                }`}><Icon className="w-4 h-4" />{tab.label}</button>
            );
          })}
        </div>
        {activeTab === TABS.EXAMENES && <ExamenesTab />}
        {activeTab === TABS.BANCO && <BancoPreguntasTab />}
        {activeTab === TABS.CATEGORIAS && <CategoriasTab />}
        {activeTab === TABS.RESULTADOS && <ResultadosTab />}
      </main>
    </div>
  );
}

// ============ EXAMENES TAB ============
function ExamenesTab() {
  const { user, getAuthHeaders } = useAuth();
  const [examenes, setExamenes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editExamen, setEditExamen] = useState(null);
  const [viewExamen, setViewExamen] = useState(null);
  const [showEnviar, setShowEnviar] = useState(null);
  const isAdmin = user?.role === "admin" || user?.role === "Administrador";
  const isGH = (user?.department || "").toLowerCase().includes("gesti") || (user?.department || "").toLowerCase().includes("rrhh");
  const canManage = isAdmin || isGH;

  const fetchExamenes = useCallback(async () => {
    try { const res = await axios.get(`${API}/examenes/lista`, getAuthHeaders()); setExamenes(res.data); } catch (e) { console.error(e); }
  }, [getAuthHeaders]);
  useEffect(() => { fetchExamenes(); }, [fetchExamenes]);

  if (viewExamen) return <ExamenDetalle examenId={viewExamen} onBack={() => { setViewExamen(null); fetchExamenes(); }} />;

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="flex justify-end"><Button data-testid="btn-nuevo-examen" onClick={() => { setEditExamen(null); setShowForm(true); }}><Plus className="w-4 h-4 mr-2" />Nuevo Examen</Button></div>
      )}
      {showForm && <ExamenForm examen={editExamen} onSave={() => { setShowForm(false); fetchExamenes(); }} onCancel={() => setShowForm(false)} />}
      {showEnviar && <EnviarExamenModal examenId={showEnviar.id} esCandidato={showEnviar.esCandidato} onClose={() => { setShowEnviar(null); fetchExamenes(); }} />}
      {examenes.length === 0 ? (
        <div className="text-center py-16 text-gray-500"><ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-40" /><p>No hay exámenes disponibles</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {examenes.map(ex => (
            <div key={ex.id} data-testid={`examen-card-${ex.id}`} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ClipboardCheck className="w-7 h-7 text-indigo-500" />
                  {ex.es_para_candidato && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><UserPlus className="w-3 h-3" />Candidato</span>}
                </div>
                {canManage && (
                  <div className="flex gap-1">
                    <button className="p-1.5 text-gray-400 hover:text-indigo-500" onClick={() => { setEditExamen(ex); setShowForm(true); }}><Edit className="w-4 h-4" /></button>
                    <button className="p-1.5 text-gray-400 hover:text-red-500" onClick={async () => { if (window.confirm("¿Eliminar este examen?")) { await axios.delete(`${API}/examenes/eliminar/${ex.id}`, getAuthHeaders()); fetchExamenes(); } }}><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{ex.titulo}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">{ex.descripcion}</p>
              <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{ex.tiempo_limite_minutos} min</span>
                <span className="flex items-center gap-1"><HelpCircle className="w-3.5 h-3.5" />{(ex.preguntas_ids || []).length} preg.</span>
              </div>
              <div className="flex items-center gap-2 text-xs mb-4">
                <span className="bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-1 rounded-full">{ex.total_asignados || 0} enviados</span>
                <span className="bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400 px-2 py-1 rounded-full">{ex.total_completados || 0} completados</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setViewExamen(ex.id)} className="flex-1"><Eye className="w-3.5 h-3.5 mr-1" />Ver</Button>
                {canManage && (
                  <Button size="sm" onClick={() => setShowEnviar({ id: ex.id, esCandidato: ex.es_para_candidato })} className="flex-1">
                    <Send className="w-3.5 h-3.5 mr-1" />{ex.es_para_candidato ? "Enviar a Candidato" : "Enviar"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ EXAMEN FORM ============
function ExamenForm({ examen, onSave, onCancel }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({
    titulo: examen?.titulo || "", descripcion: examen?.descripcion || "", tiempo_limite_minutos: examen?.tiempo_limite_minutos || 30,
    preguntas_ids: examen?.preguntas_ids || [], cantidad_preguntas: examen?.cantidad_preguntas || 0, aleatorizar: examen?.aleatorizar ?? true,
    nota_minima_aprobacion: examen?.nota_minima_aprobacion || 70, puntuacion_total: examen?.puntuacion_total || 100,
    intentos_permitidos: examen?.intentos_permitidos || 1,
    requiere_pantalla_completa: examen?.requiere_pantalla_completa ?? true, detectar_cambio_pestana: examen?.detectar_cambio_pestana ?? true,
    mostrar_resultados: examen?.mostrar_resultados ?? true, es_para_candidato: examen?.es_para_candidato || false,
    visibilidad: examen?.visibilidad || "todos",
    departamentos_permitidos: examen?.departamentos_permitidos || [],
    usuarios_permitidos: examen?.usuarios_permitidos || [],
    destinatarios_predefinidos: examen?.destinatarios_predefinidos || []
  });
  const [preguntas, setPreguntas] = useState([]);
  const [expandedCats, setExpandedCats] = useState({});

  useEffect(() => { (async () => { try { const res = await axios.get(`${API}/examenes/preguntas`, getAuthHeaders()); setPreguntas(res.data); } catch (e) { console.error(e); } })(); }, [getAuthHeaders]);

  // Group questions by category
  const preguntasPorCategoria = {};
  preguntas.forEach(p => {
    const cat = p.categoria || "Sin Categoría";
    if (!preguntasPorCategoria[cat]) preguntasPorCategoria[cat] = [];
    preguntasPorCategoria[cat].push(p);
  });
  const categoriasOrdenadas = Object.keys(preguntasPorCategoria).sort();

  const togglePregunta = (id) => setForm(prev => ({ ...prev, preguntas_ids: prev.preguntas_ids.includes(id) ? prev.preguntas_ids.filter(pid => pid !== id) : [...prev.preguntas_ids, id] }));
  const toggleCat = (cat) => setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  // Select/deselect all questions in a category
  const toggleAllInCat = (cat) => {
    const ids = preguntasPorCategoria[cat].map(p => p.id);
    const allSelected = ids.every(id => form.preguntas_ids.includes(id));
    if (allSelected) {
      setForm(prev => ({ ...prev, preguntas_ids: prev.preguntas_ids.filter(pid => !ids.includes(pid)) }));
    } else {
      setForm(prev => ({ ...prev, preguntas_ids: [...new Set([...prev.preguntas_ids, ...ids])] }));
    }
  };

  const handleVisChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const addDestinatario = () => setForm(prev => ({ ...prev, destinatarios_predefinidos: [...prev.destinatarios_predefinidos, { nombre: "", email: "" }] }));
  const removeDestinatario = (idx) => setForm(prev => ({ ...prev, destinatarios_predefinidos: prev.destinatarios_predefinidos.filter((_, i) => i !== idx) }));
  const updateDestinatario = (idx, field, value) => {
    setForm(prev => {
      const updated = [...prev.destinatarios_predefinidos];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, destinatarios_predefinidos: updated };
    });
  };

  // Calculate points per question
  const numSelected = form.preguntas_ids.length;
  const puntosPerQ = numSelected > 0 ? (form.puntuacion_total / numSelected).toFixed(2) : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.preguntas_ids.length === 0) return alert("Seleccione al menos una pregunta");
    try {
      if (examen?.id) await axios.put(`${API}/examenes/actualizar/${examen.id}`, form, getAuthHeaders());
      else await axios.post(`${API}/examenes/crear`, form, getAuthHeaders());
      onSave();
    } catch (err) { console.error(err); }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="examen-form" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
      <h3 className="font-semibold text-lg">{examen ? "Editar Examen" : "Nuevo Examen"}</h3>

      <div className={`p-4 rounded-lg border-2 ${form.es_para_candidato ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20" : "border-gray-200 dark:border-gray-700"}`}>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-candidato">
          <input type="checkbox" checked={form.es_para_candidato} onChange={e => setForm({...form, es_para_candidato: e.target.checked})} className="w-5 h-5 accent-amber-500" />
          <div>
            <span className="font-medium flex items-center gap-2"><UserPlus className="w-4 h-4 text-amber-600" />Este examen es para un candidato externo</span>
            <p className="text-xs text-gray-500 mt-0.5">Los resultados solo serán visibles para Admin y Gestión Humana.</p>
          </div>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="block text-sm font-medium mb-1">Título *</label><input data-testid="input-titulo-examen" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} required /></div>
        <div><label className="block text-sm font-medium mb-1">Tiempo Límite (min)</label><input type="number" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.tiempo_limite_minutos} onChange={e => setForm({...form, tiempo_limite_minutos: parseInt(e.target.value) || 30})} /></div>
      </div>
      <div><label className="block text-sm font-medium mb-1">Descripción</label><textarea className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" rows={2} value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} /></div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div><label className="block text-sm font-medium mb-1">Puntuación Total</label><input type="number" data-testid="input-puntuacion-total" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.puntuacion_total} onChange={e => setForm({...form, puntuacion_total: parseFloat(e.target.value) || 100})} /></div>
        <div><label className="block text-sm font-medium mb-1">Nota Mínima (%)</label><input type="number" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.nota_minima_aprobacion} onChange={e => setForm({...form, nota_minima_aprobacion: parseFloat(e.target.value) || 70})} /></div>
        <div><label className="block text-sm font-medium mb-1">Intentos</label><input type="number" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.intentos_permitidos} onChange={e => setForm({...form, intentos_permitidos: parseInt(e.target.value) || 1})} /></div>
        <div><label className="block text-sm font-medium mb-1">Cant. Preguntas</label><input type="number" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.cantidad_preguntas} onChange={e => setForm({...form, cantidad_preguntas: parseInt(e.target.value) || 0})} placeholder="0 = todas" /></div>
      </div>

      {/* Points distribution info */}
      {numSelected > 0 && (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg px-4 py-2.5 text-sm" data-testid="info-distribucion-puntos">
          <span className="font-medium text-indigo-700 dark:text-indigo-300">Distribución: </span>
          <span className="text-indigo-600 dark:text-indigo-400">{form.puntuacion_total} puntos / {numSelected} preguntas = <strong>{puntosPerQ} pts</strong> cada una</span>
        </div>
      )}

      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.aleatorizar} onChange={e => setForm({...form, aleatorizar: e.target.checked})} />Aleatorizar</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.requiere_pantalla_completa} onChange={e => setForm({...form, requiere_pantalla_completa: e.target.checked})} />Pantalla completa</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.detectar_cambio_pestana} onChange={e => setForm({...form, detectar_cambio_pestana: e.target.checked})} />Detectar cambio pestaña</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.mostrar_resultados} onChange={e => setForm({...form, mostrar_resultados: e.target.checked})} />Mostrar resultados</label>
      </div>

      <VisibilitySelector visibilidad={form.visibilidad} departamentos_permitidos={form.departamentos_permitidos} usuarios_permitidos={form.usuarios_permitidos} onChange={handleVisChange} helperEndpoint="/examenes" />

      {/* Destinatarios Predefinidos */}
      <div className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3" data-testid="destinatarios-predefinidos">
        <div className="flex items-center justify-between">
          <div>
            <label className="block text-sm font-medium">Destinatarios Predefinidos</label>
            <p className="text-xs text-gray-500">Añada destinatarios que recibirán este examen automáticamente al enviarse.</p>
          </div>
          <button type="button" onClick={addDestinatario} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="btn-add-destinatario"><Plus className="w-3.5 h-3.5" />Agregar</button>
        </div>
        {form.destinatarios_predefinidos.length > 0 && (
          <div className="space-y-2">
            {form.destinatarios_predefinidos.map((d, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input className="flex-1 border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600" placeholder="Nombre" value={d.nombre} onChange={e => updateDestinatario(idx, "nombre", e.target.value)} />
                <input className="flex-1 border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600" placeholder="Correo electrónico *" type="email" value={d.email} onChange={e => updateDestinatario(idx, "email", e.target.value)} data-testid={`input-dest-predef-${idx}`} />
                <button type="button" onClick={() => removeDestinatario(idx)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
        {form.destinatarios_predefinidos.length === 0 && <p className="text-xs text-gray-400 text-center py-2">Sin destinatarios predefinidos.</p>}
      </div>

      {/* Preguntas grouped by category */}
      <div className="border rounded-lg dark:border-gray-700" data-testid="preguntas-por-categoria">
        <div className="px-4 py-3 border-b dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded-t-lg">
          <h4 className="font-medium text-sm">Seleccionar Preguntas ({form.preguntas_ids.length} seleccionadas)</h4>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {categoriasOrdenadas.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No hay preguntas. Cree preguntas en el Banco de Preguntas.</p>
          ) : (
            categoriasOrdenadas.map(cat => {
              const catPreguntas = preguntasPorCategoria[cat];
              const selectedInCat = catPreguntas.filter(p => form.preguntas_ids.includes(p.id)).length;
              const isExpanded = expandedCats[cat];
              return (
                <div key={cat} className="border-b dark:border-gray-700 last:border-b-0">
                  <div className="flex items-center gap-2 px-4 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50" onClick={() => toggleCat(cat)}>
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                    <span className="font-medium text-sm flex-1">{cat}</span>
                    <span className="text-xs text-gray-500">{selectedInCat}/{catPreguntas.length}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); toggleAllInCat(cat); }}
                      className={`text-xs px-2 py-0.5 rounded border ${selectedInCat === catPreguntas.length ? "bg-indigo-100 text-indigo-700 border-indigo-300" : "bg-gray-100 text-gray-600 border-gray-300"}`}
                      data-testid={`btn-select-all-${cat.replace(/\s/g, '-')}`}>
                      {selectedInCat === catPreguntas.length ? "Deseleccionar" : "Seleccionar todo"}
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="px-4 pb-2 space-y-1">
                      {catPreguntas.map(p => (
                        <label key={p.id} className="flex items-start gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer">
                          <input type="checkbox" checked={form.preguntas_ids.includes(p.id)} onChange={() => togglePregunta(p.id)} className="mt-0.5" />
                          <div><p className="text-sm">{p.texto}</p><span className="text-xs text-gray-400">{p.tipo}</span></div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
      <div className="flex gap-2 justify-end"><Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button><Button type="submit" data-testid="btn-guardar-examen"><Save className="w-4 h-4 mr-2" />Guardar</Button></div>
    </form>
  );
}

// ============ ENVIAR EXAMEN MODAL ============
function EnviarExamenModal({ examenId, esCandidato, onClose }) {
  const { getAuthHeaders } = useAuth();
  const [destinatarios, setDestinatarios] = useState([{ nombre: "", email: "" }]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const addDest = () => setDestinatarios([...destinatarios, { nombre: "", email: "" }]);
  const removeDest = (idx) => setDestinatarios(destinatarios.filter((_, i) => i !== idx));
  const updateDest = (idx, field, value) => { const u = [...destinatarios]; u[idx][field] = value; setDestinatarios(u); };

  const handleEnviar = async () => {
    const valid = destinatarios.filter(d => d.email);
    if (valid.length === 0) return alert("Agregue al menos un destinatario con email");
    setLoading(true);
    try { const res = await axios.post(`${API}/examenes/enviar`, { examen_id: examenId, destinatarios: valid }, getAuthHeaders()); setResult(res.data); } catch (err) { console.error(err); alert(err.response?.data?.detail || "Error al enviar"); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="enviar-examen-modal">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            {esCandidato ? <><UserPlus className="w-5 h-5 text-amber-500" />Enviar Examen a Candidato</> : <>Enviar Examen</>}
          </h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {esCandidato && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 mb-4 text-sm text-amber-800 dark:text-amber-300">
            <p className="font-medium flex items-center gap-1"><AlertTriangle className="w-4 h-4" />Examen para Candidato</p>
            <p className="text-xs mt-1">Los resultados de este examen solo serán visibles para Admin y Gestión Humana.</p>
          </div>
        )}

        {result ? (
          <div className="text-center py-4">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="font-medium">{result.message}</p>
            <p className="text-sm text-gray-500 mt-2">Los enlaces han sido generados.</p>
            <div className="mt-4 space-y-2 text-left">
              {result.asignaciones?.map(a => (
                <div key={a.id} className="text-xs bg-gray-50 dark:bg-gray-700 p-3 rounded">
                  <p className="font-medium">{a.nombre || a.email}</p>
                  <p className="text-gray-500 break-all mt-1">{window.location.origin}/examen/{a.token}</p>
                </div>
              ))}
            </div>
            <Button onClick={onClose} className="mt-4">Cerrar</Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-3">{esCandidato ? "Ingrese los datos del candidato:" : "Ingrese los destinatarios:"}</p>
            <div className="space-y-3">
              {destinatarios.map((d, idx) => (
                <div key={idx} className="flex gap-2">
                  <input className="flex-1 border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600" placeholder="Nombre del candidato" value={d.nombre} onChange={e => updateDest(idx, "nombre", e.target.value)} />
                  <input className="flex-1 border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600" placeholder="Correo electrónico *" type="email" value={d.email} onChange={e => updateDest(idx, "email", e.target.value)} data-testid={`input-email-dest-${idx}`} />
                  {destinatarios.length > 1 && <button onClick={() => removeDest(idx)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>}
                </div>
              ))}
            </div>
            {!esCandidato && <button onClick={addDest} className="text-sm text-blue-600 hover:underline mt-2 flex items-center gap-1"><Plus className="w-3 h-3" />Agregar destinatario</button>}
            <div className="flex gap-2 justify-end mt-4">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={handleEnviar} disabled={loading} data-testid="btn-enviar-examen">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Enviar
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============ EXAMEN DETALLE ============
function ExamenDetalle({ examenId, onBack }) {
  const { user, getAuthHeaders } = useAuth();
  const [examen, setExamen] = useState(null);
  const [evaluando, setEvaluando] = useState(null);
  const [evalForm, setEvalForm] = useState({ calificacion: 0, aprobado: false, comentario_evaluador: "" });
  const [saving, setSaving] = useState(false);
  const [verRespuestas, setVerRespuestas] = useState(null); // asignacion object to view answers

  const isAdmin = user?.role === "admin" || user?.role === "Administrador";
  const isGH = (user?.department || "").toLowerCase().includes("gesti") || (user?.department || "").toLowerCase().includes("rrhh");
  const canEvaluate = isAdmin || isGH;

  const fetchExamen = useCallback(async () => {
    try { const res = await axios.get(`${API}/examenes/detalle/${examenId}`, getAuthHeaders()); setExamen(res.data); } catch (e) { console.error(e); }
  }, [examenId, getAuthHeaders]);
  useEffect(() => { fetchExamen(); }, [fetchExamen]);

  const handleEvaluar = (asignacion) => {
    setEvaluando(asignacion.id);
    setEvalForm({
      calificacion: asignacion.calificacion || 0,
      aprobado: asignacion.aprobado || false,
      comentario_evaluador: asignacion.comentario_evaluador || ""
    });
  };

  const handleGuardarEvaluacion = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/examenes/evaluar/${evaluando}`, evalForm, getAuthHeaders());
      setEvaluando(null);
      fetchExamen();
    } catch (err) { alert(err.response?.data?.detail || "Error al guardar evaluación"); }
    setSaving(false);
  };

  if (!examen) return <div className="text-center py-8">Cargando...</div>;

  // Build a map of question ID → question data for quick lookup
  const preguntasMap = {};
  (examen.preguntas || []).forEach(p => { preguntasMap[p.id] = p; });

  return (
    <div className="space-y-6" data-testid="examen-detalle">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800"><ChevronLeft className="w-4 h-4" />Volver</button>
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-xl font-bold">{examen.titulo}</h2>
          {examen.es_para_candidato && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><UserPlus className="w-3 h-3" />Candidato</span>}
        </div>
        <p className="text-gray-500 mb-4">{examen.descripcion}</p>
        <div className="flex flex-wrap gap-3 text-sm text-gray-600">
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{examen.tiempo_limite_minutos} min</span>
          <span className="flex items-center gap-1"><HelpCircle className="w-4 h-4" />{(examen.preguntas || []).length} preguntas</span>
          <span>Nota mínima: {examen.nota_minima_aprobacion}%</span>
          {examen.requiere_pantalla_completa && <span className="text-amber-600 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />Pantalla completa</span>}
        </div>
      </div>

      {/* Evaluación modal */}
      {evaluando && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md" data-testid="evaluar-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg">Evaluar Examen</h3>
              <button onClick={() => setEvaluando(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Calificación (%)</label>
                <input type="number" min="0" max="100" step="0.1" data-testid="input-calificacion-manual"
                  className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
                  value={evalForm.calificacion} onChange={e => setEvalForm({...evalForm, calificacion: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Resultado</label>
                <div className="flex gap-4">
                  <label className={`flex-1 p-3 rounded-lg border-2 cursor-pointer text-center transition-all ${evalForm.aprobado ? "border-green-500 bg-green-50 dark:bg-green-900/20" : "border-gray-200 dark:border-gray-700"}`}>
                    <input type="radio" name="resultado" className="hidden" checked={evalForm.aprobado} onChange={() => setEvalForm({...evalForm, aprobado: true})} />
                    <CheckCircle className={`w-6 h-6 mx-auto mb-1 ${evalForm.aprobado ? "text-green-600" : "text-gray-400"}`} />
                    <span className={`text-sm font-medium ${evalForm.aprobado ? "text-green-700" : "text-gray-500"}`}>Aprobado</span>
                  </label>
                  <label className={`flex-1 p-3 rounded-lg border-2 cursor-pointer text-center transition-all ${!evalForm.aprobado ? "border-red-500 bg-red-50 dark:bg-red-900/20" : "border-gray-200 dark:border-gray-700"}`}>
                    <input type="radio" name="resultado" className="hidden" checked={!evalForm.aprobado} onChange={() => setEvalForm({...evalForm, aprobado: false})} />
                    <XCircle className={`w-6 h-6 mx-auto mb-1 ${!evalForm.aprobado ? "text-red-600" : "text-gray-400"}`} />
                    <span className={`text-sm font-medium ${!evalForm.aprobado ? "text-red-700" : "text-gray-500"}`}>No Aprobado</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Comentario del Evaluador</label>
                <textarea className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" rows={3}
                  value={evalForm.comentario_evaluador} onChange={e => setEvalForm({...evalForm, comentario_evaluador: e.target.value})}
                  placeholder="Observaciones sobre el desempeño del evaluado..." />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEvaluando(null)}>Cancelar</Button>
                <Button onClick={handleGuardarEvaluacion} disabled={saving} data-testid="btn-guardar-evaluacion">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar Evaluación
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ver Respuestas Modal */}
      {verRespuestas && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ver-respuestas-modal">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <div>
                <h3 className="font-semibold text-lg">Respuestas de {verRespuestas.nombre || verRespuestas.email}</h3>
                <div className="flex items-center gap-3 mt-1 text-sm">
                  {verRespuestas.calificacion != null && (
                    <span className={`font-bold ${verRespuestas.aprobado ? "text-green-600" : "text-red-600"}`}>
                      {verRespuestas.calificacion}% - {verRespuestas.aprobado ? "Aprobado" : "No Aprobado"}
                    </span>
                  )}
                  {verRespuestas.tiempo_usado_segundos > 0 && (
                    <span className="text-gray-500">Tiempo: {Math.round(verRespuestas.tiempo_usado_segundos / 60)} min</span>
                  )}
                  {verRespuestas.puntos_obtenidos != null && (
                    <span className="text-gray-500">{verRespuestas.puntos_obtenidos}/{verRespuestas.total_puntos} pts</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canEvaluate && verRespuestas.estado === "completado" && (
                  <Button size="sm" onClick={() => { handleEvaluar(verRespuestas); setVerRespuestas(null); }} data-testid="btn-evaluar-desde-respuestas">
                    <Edit className="w-3.5 h-3.5 mr-1" />Evaluar
                  </Button>
                )}
                <button onClick={() => setVerRespuestas(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-5 space-y-4 flex-1">
              {(() => {
                const respuestas = verRespuestas.respuestas || {};
                const pregIds = Object.keys(respuestas);
                if (pregIds.length === 0) return <p className="text-center text-gray-500 py-8">No hay respuestas registradas para esta asignación.</p>;
                return pregIds.map((pid, idx) => {
                  const resp = respuestas[pid];
                  const pregunta = preguntasMap[pid];
                  const esCorrecta = resp?.correcta;
                  return (
                    <div key={pid} data-testid={`respuesta-item-${idx}`}
                      className={`border-2 rounded-lg p-4 ${esCorrecta ? "border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-900/10" : "border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-900/10"}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${esCorrecta ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
                          {esCorrecta ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 dark:text-white mb-2">
                            {idx + 1}. {pregunta?.texto || `Pregunta ${pid.slice(0, 8)}...`}
                          </p>
                          {pregunta?.tipo === "opcion_multiple" || pregunta?.tipo === "verdadero_falso" ? (
                            <div className="space-y-1.5">
                              {(pregunta?.opciones || []).map((op, oi) => {
                                const isUserAnswer = (resp?.respuesta || "").trim().toLowerCase() === op.trim().toLowerCase();
                                const isCorrectAnswer = (resp?.respuesta_correcta || "").trim().toLowerCase() === op.trim().toLowerCase();
                                return (
                                  <div key={oi} className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                                    isCorrectAnswer && isUserAnswer ? "bg-green-100 dark:bg-green-900/30 font-medium text-green-800 dark:text-green-300" :
                                    isCorrectAnswer ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" :
                                    isUserAnswer ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 font-medium" :
                                    "text-gray-600 dark:text-gray-400"
                                  }`}>
                                    {isCorrectAnswer && <CheckCircle className="w-3.5 h-3.5 text-green-600 shrink-0" />}
                                    {isUserAnswer && !isCorrectAnswer && <XCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />}
                                    {!isUserAnswer && !isCorrectAnswer && <div className="w-3.5 h-3.5 rounded-full border border-gray-300 shrink-0" />}
                                    <span>{op}</span>
                                    {isUserAnswer && <span className="text-xs ml-auto opacity-70">(respuesta del candidato)</span>}
                                    {isCorrectAnswer && !isUserAnswer && <span className="text-xs ml-auto opacity-70">(correcta)</span>}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className={`px-3 py-2 rounded text-sm ${esCorrecta ? "bg-green-100 dark:bg-green-900/30" : "bg-red-100 dark:bg-red-900/30"}`}>
                                <span className="text-xs font-medium text-gray-500 block mb-0.5">Respuesta del candidato:</span>
                                <span className={esCorrecta ? "text-green-800" : "text-red-800"}>{resp?.respuesta || "(sin respuesta)"}</span>
                              </div>
                              {!esCorrecta && (
                                <div className="px-3 py-2 rounded bg-green-100 dark:bg-green-900/30 text-sm">
                                  <span className="text-xs font-medium text-gray-500 block mb-0.5">Respuesta correcta:</span>
                                  <span className="text-green-800">{resp?.respuesta_correcta}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 shrink-0 flex justify-end">
              <Button variant="outline" onClick={() => setVerRespuestas(null)}>Cerrar</Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="font-semibold mb-4">Asignaciones y Resultados</h3>
        {(examen.asignaciones || []).length === 0 ? <p className="text-sm text-gray-500">No se ha enviado este examen aún.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Nombre</th>
                  <th className="text-left px-3 py-2 font-medium">Email</th>
                  <th className="text-left px-3 py-2 font-medium">Estado</th>
                  <th className="text-left px-3 py-2 font-medium">Calificación</th>
                  <th className="text-left px-3 py-2 font-medium">Incidencias</th>
                  <th className="text-left px-3 py-2 font-medium">Evaluado por</th>
                  <th className="text-left px-3 py-2 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {examen.asignaciones.map(a => (
                  <tr key={a.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-3 py-2">{a.nombre}</td>
                    <td className="px-3 py-2 text-gray-500">{a.email}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a.estado === "completado" ? "bg-green-100 text-green-700" : a.estado === "en_progreso" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-600"}`}>{a.estado}</span></td>
                    <td className="px-3 py-2">
                      {a.calificacion != null ? (
                        <div>
                          <span className={`font-bold ${a.aprobado ? "text-green-600" : "text-red-600"}`}>{a.calificacion}%</span>
                          {a.calificacion_manual != null && <span className="text-xs text-indigo-500 ml-1">(manual)</span>}
                        </div>
                      ) : "-"}
                    </td>
                    <td className="px-3 py-2">{(a.incidencias || []).length > 0 ? <span className="text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{a.incidencias.length}</span> : <span className="text-green-600 text-xs">Ninguna</span>}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {a.evaluado_por ? <span>{a.evaluado_por}</span> : <span className="text-gray-400">-</span>}
                      {a.comentario_evaluador && <p className="text-xs text-gray-400 mt-0.5 italic">"{a.comentario_evaluador}"</p>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        {a.estado === "completado" && (
                          <Button size="sm" variant="outline" onClick={() => setVerRespuestas(a)} data-testid={`btn-ver-respuestas-${a.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" />Respuestas
                          </Button>
                        )}
                        {canEvaluate && a.estado === "completado" && (
                          <Button size="sm" variant="outline" onClick={() => handleEvaluar(a)} data-testid={`btn-evaluar-${a.id}`}>
                            <Edit className="w-3.5 h-3.5 mr-1" />Evaluar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ BANCO PREGUNTAS ============
function BancoPreguntasTab() {
  const { getAuthHeaders } = useAuth();
  const [preguntas, setPreguntas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editPregunta, setEditPregunta] = useState(null);
  const [catFilter, setCatFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchPreguntas = useCallback(async () => { try { const res = await axios.get(`${API}/examenes/preguntas`, getAuthHeaders()); setPreguntas(res.data); } catch (e) { console.error(e); } }, [getAuthHeaders]);
  useEffect(() => { fetchPreguntas(); }, [fetchPreguntas]);

  const categorias = [...new Set(preguntas.map(p => p.categoria).filter(Boolean))];
  const filtered = catFilter ? preguntas.filter(p => p.categoria === catFilter) : preguntas;

  // Group filtered by category
  const grouped = {};
  filtered.forEach(p => { const c = p.categoria || "Sin Categoría"; if (!grouped[c]) grouped[c] = []; grouped[c].push(p); });

  const handleSeed = async () => { setLoading(true); try { const res = await axios.post(`${API}/examenes/preguntas/cargar-predeterminadas`, {}, getAuthHeaders()); toast.success(res.data.message); fetchPreguntas(); } catch (e) { toast.error(e.response?.data?.detail || "Error al cargar preguntas"); } setLoading(false); };

  return (
    <div className="space-y-4" data-testid="banco-preguntas-tab">
      <div className="flex flex-wrap gap-2 justify-between items-center">
        <select className="text-sm border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">Todas ({preguntas.length})</option>
          {categorias.map(c => <option key={c} value={c}>{c} ({preguntas.filter(p => p.categoria === c).length})</option>)}
        </select>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSeed} disabled={loading} data-testid="btn-seed-preguntas">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Cargar Preguntas Predeterminadas
          </Button>
          <Button onClick={() => { setEditPregunta(null); setShowForm(true); }} data-testid="btn-nueva-pregunta"><Plus className="w-4 h-4 mr-2" />Nueva Pregunta</Button>
        </div>
      </div>
      {showForm && <PreguntaForm pregunta={editPregunta} onSave={() => { setShowForm(false); fetchPreguntas(); }} onCancel={() => setShowForm(false)} />}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500"><HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-40" /><p>No hay preguntas</p><p className="text-sm mt-2">Use "Cargar Preguntas Aseguradoras" para precargar</p></div>
      ) : (
        <div className="space-y-4">
          {Object.keys(grouped).sort().map(cat => (
            <div key={cat} className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <Tag className="w-3.5 h-3.5" />{cat} <span className="font-normal text-gray-400">({grouped[cat].length})</span>
              </h4>
              {grouped[cat].map(p => (
                <div key={p.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${p.tipo === "opcion_multiple" ? "bg-blue-100 text-blue-600" : p.tipo === "verdadero_falso" ? "bg-green-100 text-green-600" : "bg-purple-100 text-purple-600"}`}><HelpCircle className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{p.texto}</p>
                    <div className="flex flex-wrap gap-1 mt-1">{p.opciones?.map((op, i) => <span key={i} className={`text-xs px-2 py-0.5 rounded ${op === p.respuesta_correcta ? "bg-green-100 text-green-700 font-medium" : "bg-gray-100 text-gray-500 dark:bg-gray-700"}`}>{op}</span>)}</div>
                    <div className="flex gap-2 mt-1 text-xs text-gray-400"><span>{p.tipo}</span></div>
                  </div>
                  <div className="flex gap-1">
                    <button className="p-1 text-gray-400 hover:text-blue-500" onClick={() => { setEditPregunta(p); setShowForm(true); }}><Edit className="w-4 h-4" /></button>
                    <button className="p-1 text-gray-400 hover:text-red-500" onClick={async () => { if (window.confirm("¿Eliminar?")) { await axios.delete(`${API}/examenes/preguntas/${p.id}`, getAuthHeaders()); fetchPreguntas(); } }}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ PREGUNTA FORM ============
function PreguntaForm({ pregunta, onSave, onCancel }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({ texto: pregunta?.texto || "", tipo: pregunta?.tipo || "opcion_multiple", opciones: pregunta?.opciones || ["", "", "", ""], respuesta_correcta: pregunta?.respuesta_correcta || "", categoria: pregunta?.categoria || "" });
  const [categorias, setCategorias] = useState([]);
  const [newCat, setNewCat] = useState("");

  useEffect(() => { (async () => { try { const res = await axios.get(`${API}/examenes/preguntas/categorias`, getAuthHeaders()); setCategorias(res.data); } catch (e) { console.error(e); } })(); }, [getAuthHeaders]);

  const updateOpcion = (idx, val) => { const ops = [...form.opciones]; ops[idx] = val; setForm({...form, opciones: ops}); };

  const handleAddCat = async () => {
    if (!newCat.trim()) return;
    try { const res = await axios.post(`${API}/examenes/preguntas/categorias`, { nombre: newCat.trim() }, getAuthHeaders()); setCategorias([...categorias, res.data]); setForm({...form, categoria: res.data.nombre}); setNewCat(""); } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, opciones: form.opciones.filter(o => o.trim()) };
    try { if (pregunta?.id) await axios.put(`${API}/examenes/preguntas/${pregunta.id}`, payload, getAuthHeaders()); else await axios.post(`${API}/examenes/preguntas`, payload, getAuthHeaders()); onSave(); } catch (err) { console.error(err); }
  };
  return (
    <form onSubmit={handleSubmit} data-testid="pregunta-form" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-4">
      <h4 className="font-semibold">{pregunta ? "Editar Pregunta" : "Nueva Pregunta"}</h4>
      <div><label className="block text-sm font-medium mb-1">Pregunta *</label><textarea data-testid="input-texto-pregunta" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" rows={2} value={form.texto} onChange={e => setForm({...form, texto: e.target.value})} required /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="block text-sm font-medium mb-1">Tipo</label><select className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})}><option value="opcion_multiple">Opción Múltiple</option><option value="verdadero_falso">Verdadero/Falso</option><option value="respuesta_corta">Respuesta Corta</option></select></div>
        <div>
          <label className="block text-sm font-medium mb-1">Categoría *</label>
          <div className="flex gap-2">
            <select data-testid="select-categoria" className="flex-1 border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.categoria} onChange={e => setForm({...form, categoria: e.target.value})}>
              <option value="">Seleccionar categoría...</option>
              {categorias.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
            </select>
            <div className="flex gap-1">
              <input className="border rounded-lg px-2 py-2 text-sm w-40 dark:bg-gray-700 dark:border-gray-600" placeholder="Nueva categoría" value={newCat} onChange={e => setNewCat(e.target.value)} />
              <button type="button" onClick={handleAddCat} className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-lg text-sm hover:bg-indigo-200"><Plus className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </div>
      {(form.tipo === "opcion_multiple" || form.tipo === "verdadero_falso") && (
        <div><label className="block text-sm font-medium mb-1">Opciones</label>
          {form.tipo === "verdadero_falso" ? (
            <div className="flex gap-4">{["Verdadero", "Falso"].map(op => <label key={op} className="flex items-center gap-2"><input type="radio" name="respuesta" checked={form.respuesta_correcta === op} onChange={() => setForm({...form, respuesta_correcta: op, opciones: ["Verdadero", "Falso"]})} />{op}</label>)}</div>
          ) : (
            <div className="space-y-2">
              {form.opciones.map((op, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input type="radio" name="respuesta" checked={form.respuesta_correcta === op && op !== ""} onChange={() => setForm({...form, respuesta_correcta: op})} />
                  <input className="flex-1 border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600" placeholder={`Opción ${idx + 1}`} value={op} onChange={e => updateOpcion(idx, e.target.value)} />
                  {form.opciones.length > 2 && <button type="button" onClick={() => setForm({...form, opciones: form.opciones.filter((_, i) => i !== idx)})} className="text-red-400"><X className="w-4 h-4" /></button>}
                </div>
              ))}
              <button type="button" onClick={() => setForm({...form, opciones: [...form.opciones, ""]})} className="text-sm text-blue-600 hover:underline flex items-center gap-1"><Plus className="w-3 h-3" />Agregar opción</button>
            </div>
          )}
        </div>
      )}
      {form.tipo === "respuesta_corta" && <div><label className="block text-sm font-medium mb-1">Respuesta Correcta</label><input className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.respuesta_correcta} onChange={e => setForm({...form, respuesta_correcta: e.target.value})} /></div>}
      <div className="flex gap-2 justify-end"><Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button><Button type="submit" data-testid="btn-guardar-pregunta"><Save className="w-4 h-4 mr-2" />Guardar</Button></div>
    </form>
  );
}

// ============ CATEGORÍAS TAB ============
function CategoriasTab() {
  const { getAuthHeaders } = useAuth();
  const [categorias, setCategorias] = useState([]);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => { try { const res = await axios.get(`${API}/examenes/preguntas/categorias`, getAuthHeaders()); setCategorias(res.data); } catch (e) { console.error(e); } }, [getAuthHeaders]);
  useEffect(() => { fetch(); }, [fetch]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try { await axios.post(`${API}/examenes/preguntas/categorias`, { nombre: newName.trim() }, getAuthHeaders()); setNewName(""); fetch(); } catch (e) { alert(e.response?.data?.detail || "Error"); }
    setLoading(false);
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try { await axios.put(`${API}/examenes/preguntas/categorias/${id}`, { nombre: editName.trim() }, getAuthHeaders()); setEditId(null); fetch(); } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta categoría?")) return;
    try { await axios.delete(`${API}/examenes/preguntas/categorias/${id}`, getAuthHeaders()); fetch(); } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="categorias-tab">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="font-semibold mb-4">Gestión de Categorías</h3>
        <p className="text-sm text-gray-500 mb-4">Las categorías permiten organizar las preguntas del banco por temas. Al renombrar una categoría, todas las preguntas asociadas se actualizan automáticamente.</p>
        <div className="flex gap-2 mb-6">
          <input data-testid="input-nueva-categoria" className="flex-1 border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" placeholder="Nombre de nueva categoría..." value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleCreate(); } }} />
          <Button onClick={handleCreate} disabled={loading || !newName.trim()} data-testid="btn-crear-categoria"><Plus className="w-4 h-4 mr-2" />Crear</Button>
        </div>
        {categorias.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No hay categorías. Cree una o cargue las preguntas aseguradoras.</p>
        ) : (
          <div className="space-y-2">
            {categorias.map(cat => (
              <div key={cat.id} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700 rounded-lg px-4 py-3">
                <Tag className="w-4 h-4 text-indigo-500 shrink-0" />
                {editId === cat.id ? (
                  <div className="flex gap-2 flex-1">
                    <input className="flex-1 border rounded-lg px-3 py-1.5 text-sm dark:bg-gray-600 dark:border-gray-500" value={editName} onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") handleUpdate(cat.id); if (e.key === "Escape") setEditId(null); }} autoFocus />
                    <Button size="sm" onClick={() => handleUpdate(cat.id)}><Save className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => setEditId(null)}><X className="w-3.5 h-3.5" /></Button>
                  </div>
                ) : (
                  <>
                    <span className="flex-1 text-sm font-medium">{cat.nombre}</span>
                    <button className="p-1 text-gray-400 hover:text-blue-500" onClick={() => { setEditId(cat.id); setEditName(cat.nombre); }} data-testid={`btn-edit-cat-${cat.id}`}><Edit className="w-4 h-4" /></button>
                    <button className="p-1 text-gray-400 hover:text-red-500" onClick={() => handleDelete(cat.id)} data-testid={`btn-delete-cat-${cat.id}`}><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ RESULTADOS TAB ============
function ResultadosTab() {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [examenes, setExamenes] = useState([]);
  useEffect(() => { (async () => { try { const [s, e] = await Promise.all([axios.get(`${API}/examenes/estadisticas-generales`, getAuthHeaders()), axios.get(`${API}/examenes/lista`, getAuthHeaders())]); setStats(s.data); setExamenes(e.data); } catch (e) { console.error(e); } })(); }, [getAuthHeaders]);

  return (
    <div className="space-y-6" data-testid="resultados-tab">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total Exámenes", value: stats.total_examenes, color: "bg-blue-100 text-blue-600" },
            { label: "Preguntas", value: stats.total_preguntas, color: "bg-indigo-100 text-indigo-600" },
            { label: "Completados", value: stats.total_completados, color: "bg-green-100 text-green-600" },
            { label: "Tasa Aprobación", value: `${stats.tasa_aprobacion}%`, color: "bg-amber-100 text-amber-600" }
          ].map((s, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>
      )}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="font-semibold mb-4">Resumen por Examen</h3>
        {examenes.length === 0 ? <p className="text-sm text-gray-500">No hay exámenes aún</p> : (
          <div className="space-y-3">{examenes.map(ex => (
            <div key={ex.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center gap-2">
                <div><h4 className="font-medium text-sm">{ex.titulo}</h4><p className="text-xs text-gray-500">{(ex.preguntas_ids || []).length} preguntas - {ex.tiempo_limite_minutos} min</p></div>
                {ex.es_para_candidato && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Candidato</span>}
              </div>
              <div className="flex gap-4 text-sm"><span className="text-blue-600">{ex.total_asignados || 0} enviados</span><span className="text-green-600">{ex.total_completados || 0} completados</span></div>
            </div>
          ))}</div>
        )}
      </div>
    </div>
  );
}
